import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  ClipboardList,
  Archive,
  BookOpenText,
  BarChart3,
  ShieldCheck,
  Plus,
  Crown,
  Moon,
  Sun,
  LogOut,
  MessageSquareText,
  CornerDownLeft,
} from 'lucide-react';
import { GeneratedCampaign, User } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../i18n/translations';

type Tab = 'builder' | 'saved' | 'library' | 'insights' | 'admin';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: React.ElementType;
  action: () => void;
  group: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  setActiveTab: (tab: Tab) => void;
  onNewProject: () => void;
  onOpenPricing: () => void;
  onLogout: () => void;
  currentUser: User | null;
  savedCampaigns: GeneratedCampaign[];
  onSelectCampaign: (c: GeneratedCampaign) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  setActiveTab,
  onNewProject,
  onOpenPricing,
  onLogout,
  currentUser,
  savedCampaigns,
  onSelectCampaign,
}) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [isOpen]);

  const items: CommandItem[] = useMemo(() => {
    const base: CommandItem[] = [
      { id: 'new', label: t('nav.newProject'), icon: Plus, action: onNewProject, group: t('cmd.quickActions') },
      { id: 'builder', label: t('cmd.goToBuilder'), icon: ClipboardList, action: () => setActiveTab('builder'), group: t('cmd.navigation') },
      { id: 'saved', label: t('nav.saved'), icon: Archive, action: () => setActiveTab('saved'), group: t('cmd.navigation') },
      { id: 'library', label: t('nav.library'), icon: BookOpenText, action: () => setActiveTab('library'), group: t('cmd.navigation') },
      { id: 'insights', label: t('nav.insights'), icon: BarChart3, action: () => setActiveTab('insights'), group: t('cmd.navigation') },
      { id: 'pricing', label: t('nav.upgradePlan'), icon: Crown, action: onOpenPricing, group: t('cmd.quickActions') },
      {
        id: 'theme',
        label: theme === 'dark' ? t('cmd.switchToLight') : t('cmd.switchToDark'),
        icon: theme === 'dark' ? Sun : Moon,
        action: toggleTheme,
        group: t('cmd.quickActions'),
      },
    ];
    if (currentUser?.role === 'admin') {
      base.splice(4, 0, {
        id: 'admin',
        label: t('nav.admin'),
        icon: ShieldCheck,
        action: () => setActiveTab('admin'),
        group: t('cmd.navigation'),
      });
    }
    if (currentUser) {
      base.push({ id: 'logout', label: t('nav.logout'), icon: LogOut, action: onLogout, group: t('cmd.quickActions') });
    }
    const history: CommandItem[] = savedCampaigns.slice(0, 8).map((c) => ({
      id: `camp_${c.id}`,
      label: c.projectInfo.projectName,
      hint: t('cmd.openProject'),
      icon: MessageSquareText,
      action: () => onSelectCampaign(c),
      group: t('nav.recentChats'),
    }));
    return [...base, ...history];
  }, [theme, currentUser, savedCampaigns, onNewProject, onOpenPricing, onLogout, toggleTheme, setActiveTab, onSelectCampaign, t]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [items, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    filtered.forEach((item) => {
      if (!map.has(item.group)) map.set(item.group, []);
      map.get(item.group)!.push(item);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const runItem = (item: CommandItem) => {
    item.action();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) runItem(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  let flatIndex = -1;

  return (
    <div className="command-palette fixed inset-0 z-[90] flex items-start justify-center pt-[9vh] px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn" onClick={onClose} />
      <div
        className="relative w-full max-w-2xl card !rounded-[26px] overflow-hidden animate-scaleIn shadow-[var(--shadow-pop)]"
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label={t('cmd.searchPlaceholder')}
      >
        <div className="command-search flex items-center gap-3 px-5 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
          <Search className="w-4.5 h-4.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder={t('cmd.searchPlaceholder')}
            className="flex-1 bg-transparent py-3.5 text-sm font-bold outline-none"
            style={{color: 'var(--text-primary)', outline: 'none', border: 'none', boxShadow: 'none'}}
          />
          <kbd
            className="text-[10px] font-black px-1.5 py-0.5 rounded-md"
            style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-muted)' }}
          >
            Esc
          </kbd>
        </div>

        <div className="max-h-[62vh] overflow-y-auto p-3">
          {grouped.length === 0 && (
            <p className="text-sm text-center py-8 font-bold" style={{ color: 'var(--text-muted)' }}>
              {t('cmd.noResults')}
            </p>
          )}
          {grouped.map(([group, groupItems]) => (
            <div key={group} className="mb-2 last:mb-0">
              <p
                className="text-[10px] font-black uppercase tracking-wide px-2.5 py-1.5"
                style={{ color: 'var(--text-muted)' }}
              >
                {group}
              </p>
              {groupItems.map((item) => {
                flatIndex += 1;
                const isActive = flatIndex === activeIndex;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onMouseEnter={() => setActiveIndex(flatIndex)}
                    onClick={() => runItem(item)}
                    className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-2xl text-sm font-bold text-right transition-colors"
                    style={isActive ? { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' } : { color: 'var(--text-secondary)' }}
                  >
                    <Icon className="w-4 h-4 shrink-0" style={isActive ? { color: 'var(--color-brand-600)' } : undefined} />
                    <span className="flex-1 truncate">{item.label}</span>
                    {isActive && <CornerDownLeft className="w-3.5 h-3.5 shrink-0 opacity-50" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
